import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown, ArrowUpRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { C, COMPANY, IMAGES } from "@/data/content";
import { scrollToId } from "@/utils/scroll";
import RouteMap from "@/components/RouteMap";

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
    const { L } = useLanguage();
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start start", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
    const mapY = useTransform(scrollYProgress, [0, 1], ["0%", "-14%"]);

    const maskedLine = (content, i) => (
        <span key={i} className="block overflow-hidden pb-1">
            <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay: 0.3 + i * 0.18, ease: EASE }}
            >
                {content}
            </motion.span>
        </span>
    );

    return (
        <section
            id="home"
            ref={ref}
            className="relative flex min-h-screen items-center overflow-hidden"
        >
            {/* parallax backdrop */}
            <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
                <img
                    src={IMAGES.hero}
                    alt=""
                    className="h-full w-full object-cover opacity-25"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-ink/75 via-ink/85 to-ink" />
            </motion.div>

            {/* trade routes motif */}
            <motion.div
                style={{ y: mapY }}
                className="pointer-events-none absolute inset-y-0 end-0 hidden w-[48%] items-center opacity-45 md:flex"
            >
                <RouteMap labels={C.markets.nodes} className="w-full" />
            </motion.div>

            <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-24 pt-36">
                <motion.p
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.15 }}
                    className="font-mono-x text-[11px] tracking-[0.35em] text-brass uppercase sm:text-xs"
                >
                    {L(C.hero.eyebrow)}
                </motion.p>

                <h1 className="font-display mt-6 max-w-3xl text-4xl font-bold leading-[1.18] text-txt-1 sm:text-5xl md:text-6xl">
                    {maskedLine(L(C.hero.line1), 0)}
                    {maskedLine(
                        <>
                            {L(C.hero.line2plain)}
                            <em className="italic text-brass">
                                {L(C.hero.line2accent)}
                            </em>
                        </>,
                        1
                    )}
                </h1>

                <motion.span
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
                    className="mt-8 block h-px w-28 origin-left bg-gradient-to-r from-brass to-transparent rtl:origin-right"
                />

                <motion.p
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 1, ease: EASE }}
                    className="mt-7 max-w-xl text-base leading-relaxed text-txt-2 sm:text-lg"
                >
                    {L(C.hero.sub)}
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 1.15, ease: EASE }}
                    className="mt-10 flex flex-wrap items-center gap-4"
                >
                    <button
                        data-testid="hero-cta-contact"
                        onClick={() => scrollToId("contact")}
                        className="group flex items-center gap-2 rounded-full bg-brass px-7 py-3.5 text-sm font-bold text-ink transition-transform hover:scale-105"
                    >
                        {L(C.hero.ctaPrimary)}
                        <ArrowUpRight className="size-4 transition-transform group-hover:rotate-45" />
                    </button>
                    <button
                        data-testid="hero-cta-services"
                        onClick={() => scrollToId("services")}
                        className="rounded-full border border-gold-line-strong px-7 py-3.5 text-sm font-semibold text-brass-bright transition-colors hover:border-brass hover:bg-brass/10"
                    >
                        {L(C.hero.ctaSecondary)}
                    </button>
                </motion.div>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.4 }}
                    className="font-mono-x mt-14 text-[10px] tracking-[0.25em] text-txt-3"
                    data-testid="hero-coordinates"
                >
                    {COMPANY.coords} — {C.hero.hubs}
                </motion.p>
            </div>

            <button
                data-testid="hero-scroll-cue"
                onClick={() => scrollToId("about")}
                aria-label={L(C.hero.scroll)}
                className="float-y absolute bottom-7 left-1/2 z-10 -translate-x-1/2 text-brass"
            >
                <ChevronDown className="size-6" />
            </button>
        </section>
    );
}
